import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail, formatApiError } from "@/lib/api";
import { SeeItInAction } from "@/components/SeeItInAction";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, NotebookPen, Plus, Sparkles, Trash2, Save, ClipboardList, Paperclip, Download, CheckCircle2, FileText, FileDown, BookmarkPlus, Mail, Upload, Star, AlertTriangle, Scale,
} from "lucide-react";
import { formatBytes } from "@/components/ArtifactsView";

const MEETING_PALETTE = ["#2563EB", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0891B2", "#DB2777", "#4F46E5"];

export default function NotebookView({ projectId }) {
  const [meetings, setMeetings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [defaultTpl, setDefaultTpl] = useState(null);
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = async (keepId) => {
    try {
      const [m, t, p] = await Promise.all([api.get(`/projects/${projectId}/meetings`), api.get("/meeting-templates"), api.get(`/projects/${projectId}`)]);
      setMeetings(m.data);
      setTemplates(t.data);
      setDefaultTpl(p.data.default_minutes_template || null);
      setSelected((prev) => {
        const id = keepId || prev;
        return m.data.some((x) => x.id === id) ? id : m.data[0]?.id || null;
      });
    } catch (e) {
      toast.error("Could not load notebook");
      setMeetings([]);
    }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!meetings) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const meeting = meetings.find((m) => m.id === selected);

  return (
    <div className="space-y-5" data-testid="notebook-view">
      <div className="flex flex-wrap items-center gap-2">
        <SeeItInAction chapter={13} className="ml-auto order-last" />
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0" data-testid="notebook-tabs">
          {[...meetings].sort((a, b) => (b.meeting_date || "").localeCompare(a.meeting_date || "")).map((m) => {
            const c = MEETING_PALETTE[[...m.id].reduce((a, ch) => a + ch.charCodeAt(0), 0) % MEETING_PALETTE.length];
            const active = m.id === selected;
            return (
              <button key={m.id} onClick={() => setSelected(m.id)}
                className="px-3 py-1.5 rounded-full text-xs font-bold border transition-all max-w-[240px] truncate hover:-translate-y-px"
                style={active ? { background: c, borderColor: c, color: "#fff", boxShadow: `0 2px 8px ${c}55` }
                              : { background: `${c}14`, borderColor: `${c}55`, color: c }}
                data-testid={`notebook-tab-${m.id}`}>
                {m.meeting_date} · {m.title}
              </button>
            );
          })}
          {meetings.length === 0 && <p className="text-sm text-muted-foreground py-1">No meetings yet — create your first notebook tab.</p>}
        </div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setNewOpen(true)} data-testid="notebook-new-meeting"><Plus className="h-3.5 w-3.5" /> New meeting</Button>
      </div>

      {meeting && <MeetingSheet key={meeting.id} meeting={meeting} templates={templates} projectId={projectId} defaultTpl={defaultTpl} setDefaultTpl={setDefaultTpl} onChanged={load} />}

      <NewMeetingDialog open={newOpen} onOpenChange={setNewOpen} projectId={projectId}
        onCreated={(m) => { setNewOpen(false); load(m.id); }} />
    </div>
  );
}

function MeetingSheet({ meeting, templates, projectId, defaultTpl, setDefaultTpl, onChanged }) {
  const [minutes, setMinutes] = useState(meeting.minutes || "");
  const [transcript, setTranscript] = useState("");
  const [template, setTemplate] = useState(meeting.template || defaultTpl || "standard");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [checked, setChecked] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const extracted = meeting.extracted_actions || [];
  const extractedRisks = meeting.extracted_risks || [];
  const extractedDecisions = meeting.extracted_decisions || [];
  const [checkedR, setCheckedR] = useState({});
  const [checkedD, setCheckedD] = useState({});

  useEffect(() => {
    api.get(`/meetings/${meeting.id}/artifacts`).then(({ data }) => setFiles(data)).catch(() => {});
  }, [meeting.id]);

  useEffect(() => {
    const init = {};
    extracted.forEach((a, i) => { if (!a.created) init[i] = true; });
    setChecked(init);
    const initR = {};
    extractedRisks.forEach((r, i) => { if (!r.created) initR[i] = true; });
    setCheckedR(initR);
    const initD = {};
    extractedDecisions.forEach((d, i) => { if (!d.created) initD[i] = true; });
    setCheckedD(initD);
  }, [meeting.id, extracted.length, extractedRisks.length, extractedDecisions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMinutes = async () => {
    setSaving(true);
    try {
      await api.put(`/meetings/${meeting.id}`, { minutes });
      toast.success("Minutes saved");
      onChanged(meeting.id);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const insertTemplate = () => {
    if (!templates.length) {
      toast.error("Templates are still loading — try again in a moment");
      return;
    }
    const t = templates.find((x) => x.key === template) || templates[0];
    setMinutes((m) => (m.trim() ? m + "\n\n" + t.body : t.body));
    toast.success(`"${t.label}" template inserted`);
  };

  const selectedTemplate = templates.find((x) => x.key === template);

  const isDefault = template === defaultTpl;
  const toggleDefault = async () => {
    const next = isDefault ? null : template;
    try {
      await api.patch(`/projects/${projectId}/default-template`, { template: next });
      setDefaultTpl(next);
      const label = templates.find((x) => x.key === template)?.label || template;
      toast.success(next ? `"${label}" is now this project's default minutes template` : "Default template cleared");
    } catch (e) { toast.error("Could not update default template"); }
  };

  const tplFileRef = useRef(null);
  const [uploadingTpl, setUploadingTpl] = useState(false);
  const uploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (ext === "doc") {
      toast.error("Legacy .doc files aren't supported — open it in Word and save it as .docx, then upload again.", { duration: 8000 });
      return;
    }
    if (!["docx", "pptx", "pdf", "txt", "md", "markdown"].includes(ext)) {
      toast.error(`".${ext || file.name}" files aren't supported — upload a .docx, .pptx, .pdf, .txt or .md file.`, { duration: 8000 });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`"${file.name}" is ${(file.size / 1048576).toFixed(1)} MB — the limit is 5 MB. Remove images or split the file and try again.`, { duration: 8000 });
      return;
    }
    setUploadingTpl(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/meeting-templates/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success(`Template "${data.label}" uploaded — select it and click "Insert template", or use it with AI minutes`);
      await onChanged(meeting.id);
      setTemplate(data.key);
    } catch (err) {
      toast.error(formatApiError(err, `Could not upload "${file.name}"`), { duration: 10000 });
    } finally { setUploadingTpl(false); }
  };

  const deleteTemplate = async () => {
    if (!selectedTemplate?.custom) return;
    if (!window.confirm(`Delete your "${selectedTemplate.label}" template?`)) return;
    try {
      await api.delete(`/meeting-templates/${selectedTemplate.key}`);
      toast.success("Template deleted");
      setTemplate("standard");
      onChanged(meeting.id);
    } catch (e) { toast.error("Could not delete template"); }
  };

  const emailAttendees = async () => {
    if (!minutes.trim()) { toast.info("Write or generate minutes first"); return; }
    if (!(meeting.attendees || "").trim()) { toast.info("Add attendees to the meeting first"); return; }
    setEmailing(true);
    try {
      await api.put(`/meetings/${meeting.id}`, { minutes });
      const { data } = await api.post(`/meetings/${meeting.id}/email-minutes`);
      if (data.sent) {
        toast.success(`Minutes + PDF sent to ${data.recipients.length} attendee(s)` +
          (data.unresolved?.length ? ` — no email found for: ${data.unresolved.join(", ")}` : ""));
      } else {
        toast.error(data.error || "Could not send the minutes");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the minutes");
    } finally { setEmailing(false); }
  };

  const downloadPdf = async () => {
    if (!minutes.trim()) { toast.info("Write or generate minutes first"); return; }
    setDownloading(true);
    try {
      await api.put(`/meetings/${meeting.id}`, { minutes });
      const { data } = await api.get(`/meetings/${meeting.id}/minutes-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${meeting.title.replace(/[^a-z0-9-_ ]/gi, "")}-minutes.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate the PDF");
    } finally { setDownloading(false); }
  };

  const trFileRef = useRef(null);
  const [uploadingTr, setUploadingTr] = useState(false);
  const uploadTranscript = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingTr(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/transcript-file`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setTranscript((t) => (t.trim() ? t + "\n\n" + data.text : data.text));
      toast.success(`Transcript from "${data.filename}" loaded — review it, then draft the minutes`);
    } catch (err) {
      toast.error(formatApiError(err, `Could not read "${file.name}"`), { duration: 8000 });
    } finally { setUploadingTr(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/ai-minutes`, { transcript, template });
      setMinutes(data.minutes || "");
      toast.success(`Minutes drafted — ${(data.extracted_actions || []).length} action item(s), ${(data.extracted_risks || []).length} risk(s) found`);
      onChanged(meeting.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const createActions = async () => {
    const indexes = Object.entries(checked).filter(([, v]) => v).map(([k]) => Number(k));
    if (!indexes.length) { toast.info("Select at least one action item"); return; }
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/create-actions`, { indexes });
      toast.success(`${data.created} action item(s) added to the register`);
      onChanged(meeting.id);
    } catch (e) {
      toast.error("Could not create action items");
    }
  };

  const createRisks = async () => {
    const indexes = Object.entries(checkedR).filter(([, v]) => v).map(([k]) => Number(k));
    if (!indexes.length) { toast.info("Select at least one risk"); return; }
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/create-risks`, { indexes });
      toast.success(`${data.created} risk(s) added to the risk log`);
      onChanged(meeting.id);
    } catch (e) {
      toast.error("Could not create risks");
    }
  };

  const createDecisions = async () => {
    const indexes = Object.entries(checkedD).filter(([, v]) => v).map(([k]) => Number(k));
    if (!indexes.length) { toast.info("Select at least one decision"); return; }
    try {
      const { data } = await api.post(`/meetings/${meeting.id}/create-decisions`, { indexes });
      toast.success(`${data.created} decision point(s) added to RAID ▸ Decisions`);
      onChanged(meeting.id);
    } catch (e) {
      toast.error("Could not create decisions");
    }
  };

  const upload = async (fs) => {
    if (!fs?.length) return;
    setUploading(true);
    for (const f of fs) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", "Meeting Notes");
      fd.append("meeting_id", meeting.id);
      try {
        await api.post(`/projects/${projectId}/artifacts`, fd);
        toast.success(`${f.name} attached`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    api.get(`/meetings/${meeting.id}/artifacts`).then(({ data }) => setFiles(data)).catch(() => {});
  };

  const download = async (a) => {
    try {
      const { data } = await api.get(`/artifacts/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url; link.download = a.original_filename; link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const removeMeeting = async () => {
    if (!window.confirm(`Delete "${meeting.title}" and its minutes?`)) return;
    try {
      await api.delete(`/meetings/${meeting.id}`);
      toast.success("Meeting deleted");
      onChanged();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid={`meeting-sheet-${meeting.id}`}>
      {/* Minutes editor */}
      <div className="lg:col-span-2 border border-border rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <NotebookPen className="h-4 w-4 text-primary" />
          <h3 className="font-display font-bold text-sm flex-1 truncate">{meeting.title} <span className="text-muted-foreground font-normal">· {meeting.meeting_date}</span></h3>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="notebook-template-select"><SelectValue /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.custom ? `${t.label} · mine` : t.label}{t.key === defaultTpl ? " ★" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className={isDefault ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground"} onClick={toggleDefault}
            title={isDefault ? "This is the project's default template — click to clear" : "Make this the default template for all meetings in this project"} data-testid="notebook-default-template-toggle">
            <Star className={`h-4 w-4 ${isDefault ? "fill-current" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={insertTemplate} data-testid="notebook-insert-template">Insert template</Button>
          <input ref={tplFileRef} type="file" accept=".docx,.pptx,.pdf,.txt,.md,.markdown" className="hidden" onChange={uploadTemplate} data-testid="notebook-upload-template-input" />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => tplFileRef.current?.click()} disabled={uploadingTpl} title="Upload a .docx, .pdf, .txt or .md file to use as a minutes template" data-testid="notebook-upload-template">
            {uploadingTpl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
          </Button>
          {selectedTemplate?.custom && (
            <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={deleteTemplate} title="Delete this custom template" data-testid="notebook-delete-template">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={removeMeeting} data-testid="notebook-delete-meeting"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
        {meeting.attendees && <p className="text-xs text-muted-foreground">Attendees: {meeting.attendees}</p>}
        <textarea
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          rows={18}
          placeholder="Write minutes here, insert a template, or paste a transcript on the right and let AI draft them…"
          className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-mono leading-relaxed resize-y"
          data-testid="notebook-minutes"
        />
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setSaveTplOpen(true)} disabled={!minutes.trim()} data-testid="notebook-save-as-template">
            <BookmarkPlus className="h-3.5 w-3.5" /> Save as template
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={downloadPdf} disabled={downloading} data-testid="notebook-download-pdf">
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Download PDF
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={emailAttendees} disabled={emailing} data-testid="notebook-email-attendees">
            {emailing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email to attendees
          </Button>
          <Button size="sm" className="gap-1.5" onClick={saveMinutes} disabled={saving} data-testid="notebook-save-minutes">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save minutes
          </Button>
        </div>
        <SaveMinutesTemplateDialog open={saveTplOpen} onOpenChange={setSaveTplOpen} body={minutes}
          onSaved={(t) => { setSaveTplOpen(false); setTemplate(t.key); onChanged(meeting.id); }} />
      </div>

      <div className="space-y-5">
        {/* AI transcript box */}
        <div className="border border-border rounded-xl p-4 space-y-3" data-testid="notebook-ai-box">
          <h3 className="font-display font-bold text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> AI minutes from transcript</h3>
          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={7}
            placeholder="Paste your meeting transcript or raw notes here…"
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm resize-y"
            data-testid="notebook-transcript"
          />
          <div className="flex items-center gap-2">
            <input ref={trFileRef} type="file" accept=".docx,.pptx,.pdf,.txt,.md,.markdown" className="hidden" onChange={uploadTranscript} data-testid="notebook-transcript-file-input" />
            <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => trFileRef.current?.click()} disabled={uploadingTr} title="Upload a .docx, .pptx, .pdf, .txt or .md transcript — its text fills the box above" data-testid="notebook-upload-transcript">
              {uploadingTr ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload transcript doc
            </Button>
          </div>
          <Button className="w-full gap-2" onClick={generate} disabled={generating || !transcript.trim()} data-testid="notebook-generate">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {generating ? "Drafting…" : "Draft minutes + find action items"}
          </Button>
        </div>

        {/* Extracted actions */}
        {extracted.length > 0 && (
          <div className="border border-border rounded-xl p-4 space-y-2" data-testid="notebook-extracted">
            <h3 className="font-display font-bold text-sm flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" /> Action items found <span className="text-xs text-muted-foreground font-normal">{extracted.length}</span></h3>
            {extracted.map((a, i) => (
              <label key={i} className={`flex items-start gap-2 text-sm rounded-lg px-2 py-1.5 ${a.created ? "opacity-60" : "hover:bg-secondary/40 cursor-pointer"}`} data-testid={`notebook-action-${i}`}>
                {a.created ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> : (
                  <input type="checkbox" checked={!!checked[i]} onChange={(e) => setChecked((c) => ({ ...c, [i]: e.target.checked }))} className="mt-1 accent-primary" data-testid={`notebook-action-check-${i}`} />
                )}
                <span className="min-w-0">
                  <span className="block">{a.title}</span>
                  <span className="text-xs text-muted-foreground">{a.owner || "Unassigned"}{a.due_date ? ` · due ${a.due_date}` : ""}{a.created ? " · added" : ""}</span>
                </span>
              </label>
            ))}
            {extracted.some((a) => !a.created) && (
              <Button size="sm" className="w-full gap-1.5" onClick={createActions} data-testid="notebook-create-actions">
                <Plus className="h-3.5 w-3.5" /> Add selected to Action Items
              </Button>
            )}
          </div>
        )}

        {/* Extracted risks */}
        {extractedRisks.length > 0 && (
          <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-4 space-y-2" data-testid="notebook-extracted-risks">
            <h3 className="font-display font-bold text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Risks identified <span className="text-xs text-muted-foreground font-normal">{extractedRisks.length}</span></h3>
            {extractedRisks.map((r, i) => (
              <label key={i} className={`flex items-start gap-2 text-sm rounded-lg px-2 py-1.5 ${r.created ? "opacity-60" : "hover:bg-amber-100/60 cursor-pointer"}`} data-testid={`notebook-risk-${i}`}>
                {r.created ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> : (
                  <input type="checkbox" checked={!!checkedR[i]} onChange={(e) => setCheckedR((c) => ({ ...c, [i]: e.target.checked }))} className="mt-1 accent-primary" data-testid={`notebook-risk-check-${i}`} />
                )}
                <span className="min-w-0">
                  <span className="block">{r.title}</span>
                  <span className="text-xs text-muted-foreground">{r.owner || "Unassigned"} · P{r.probability}×I{r.impact}{r.mitigation ? ` · ${r.mitigation.slice(0, 60)}` : ""}{r.created ? " · added" : ""}</span>
                </span>
              </label>
            ))}
            {extractedRisks.some((r) => !r.created) && (
              <Button size="sm" className="w-full gap-1.5" onClick={createRisks} data-testid="notebook-create-risks">
                <Plus className="h-3.5 w-3.5" /> Add selected to Risk Log
              </Button>
            )}
          </div>
        )}

        {/* Extracted decisions */}
        {extractedDecisions.length > 0 && (
          <div className="border border-violet-200 bg-violet-50/40 rounded-xl p-4 space-y-2" data-testid="notebook-extracted-decisions">
            <h3 className="font-display font-bold text-sm flex items-center gap-2"><Scale className="h-4 w-4 text-violet-500" /> Decisions needed <span className="text-xs text-muted-foreground font-normal">{extractedDecisions.length}</span></h3>
            {extractedDecisions.map((d, i) => (
              <label key={i} className={`flex items-start gap-2 text-sm rounded-lg px-2 py-1.5 ${d.created ? "opacity-60" : "hover:bg-violet-100/60 cursor-pointer"}`} data-testid={`notebook-decision-${i}`}>
                {d.created ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> : (
                  <input type="checkbox" checked={!!checkedD[i]} onChange={(e) => setCheckedD((c) => ({ ...c, [i]: e.target.checked }))} className="mt-1 accent-primary" data-testid={`notebook-decision-check-${i}`} />
                )}
                <span className="min-w-0">
                  <span className="block">{d.blocking && <span className="text-[9px] font-bold uppercase bg-violet-600 text-white rounded-full px-1.5 py-0.5 mr-1.5">Blocking</span>}{d.title}</span>
                  <span className="text-xs text-muted-foreground">{d.owner || "No decider"}{d.due_date ? ` · needed by ${d.due_date}` : ""}{d.created ? " · added" : ""}</span>
                </span>
              </label>
            ))}
            {extractedDecisions.some((d) => !d.created) && (
              <Button size="sm" className="w-full gap-1.5" onClick={createDecisions} data-testid="notebook-create-decisions">
                <Plus className="h-3.5 w-3.5" /> Add selected to RAID Decisions
              </Button>
            )}
          </div>
        )}

        {/* Attachments */}
        <div className="border border-border rounded-xl p-4 space-y-2" data-testid="notebook-files">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-primary" />
            <h3 className="font-display font-bold text-sm flex-1">Recordings & files</h3>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="notebook-upload">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Attach
            </Button>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="notebook-file-input" />
          </div>
          {files.length === 0 ? <p className="text-xs text-muted-foreground">Attach recordings, decks or notes (up to 50 MB each). They also appear in Artifacts.</p> : files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-sm" data-testid={`notebook-file-${f.id}`}>
              <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="truncate flex-1">{f.original_filename}</span>
              <span className="text-xs text-muted-foreground shrink-0">{formatBytes(f.size)}</span>
              <button onClick={() => download(f)} className="text-primary hover:text-primary/70" title="Download" data-testid={`notebook-download-${f.id}`}><Download className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SaveMinutesTemplateDialog({ open, onOpenChange, body, onSaved }) {
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setLabel(""); }, [open]);

  const save = async () => {
    if (!label.trim()) return toast.error("Give your template a name");
    setSaving(true);
    try {
      const { data } = await api.post("/meeting-templates", { label: label.trim(), body });
      toast.success(`Template "${data.label}" saved — it's now in your template list`);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save template");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="notebook-save-template-dialog">
        <DialogHeader><DialogTitle className="font-display">Save current notes as a template</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="tpl-name">Template name</Label>
            <Input id="tpl-name" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. My weekly sync format" className="mt-1.5" data-testid="notebook-template-name" />
          </div>
          <p className="text-xs text-muted-foreground">The full text currently in the minutes editor will be saved and available in the template dropdown for every meeting.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="notebook-template-save-submit">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Save template</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewMeetingDialog({ open, onOpenChange, projectId, onCreated }) {
  const [form, setForm] = useState({ title: "", meeting_date: new Date().toISOString().slice(0, 10), attendees: "" });
  useEffect(() => { if (open) setForm({ title: "", meeting_date: new Date().toISOString().slice(0, 10), attendees: "" }); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/projects/${projectId}/meetings`, form);
      toast.success("Meeting tab created");
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create meeting");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="notebook-new-dialog">
        <DialogHeader><DialogTitle className="font-display">New meeting</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="nm-title">Title</Label>
            <Input id="nm-title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Weekly status — Sprint 12" className="mt-1.5" data-testid="notebook-new-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="nm-date">Date</Label>
              <Input id="nm-date" type="date" required value={form.meeting_date} onChange={(e) => setForm((f) => ({ ...f, meeting_date: e.target.value }))} className="mt-1.5" data-testid="notebook-new-date" />
            </div>
            <div>
              <Label htmlFor="nm-att">Attendees</Label>
              <Input id="nm-att" value={form.attendees} onChange={(e) => setForm((f) => ({ ...f, attendees: e.target.value }))} placeholder="Names, comma-separated" className="mt-1.5" data-testid="notebook-new-attendees" />
            </div>
          </div>
          <div className="flex justify-end"><Button type="submit" data-testid="notebook-new-submit">Create</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
